// File: frontend/src/pages/ArticleDetailPage.tsx
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  ArrowLeft,
  CalendarDays,
  Tag,
  Loader2,
  FileWarning,
} from "lucide-react"; // Removed UserCircle as it's not directly used for icon

import { type Article as ArticleDetailType, type TagPublic } from "@/types";
import { getArticleByIdOrSlug } from "@/api/articleService";
import axios from "axios";

const placeholderAuthorImage = (authorName?: string | null) => {
  const initials = authorName
    ? authorName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
    : "VT";
  let hash = 0;
  for (let i = 0; i < initials.length; i++) {
    hash = initials.charCodeAt(i) + ((hash << 5) - hash);
  }
  const color = `hsl(${hash % 360}, 70%, 80%)`;
  const textColor = `hsl(${hash % 360}, 70%, 30%)`;
  return {
    fallbackBackgroundColor: color,
    fallbackTextColor: textColor,
    initials,
  };
};

function unescapeHtml(html: string): string {
  if (typeof document === 'undefined') {
    return html
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#039;/g, "'");
  }
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

const ArticleDetailPage = () => {
  const { articleSlug } = useParams<{ articleSlug: string }>();
  const [article, setArticle] = useState<ArticleDetailType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Define backendBaseUrl for constructing image paths
  const backendBaseUrl = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:8000';

  useEffect(() => {
    const fetchArticle = async () => {
      if (!articleSlug) {
        setError("Article slug is missing.");
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const fetchedArticle = await getArticleByIdOrSlug(articleSlug);
        setArticle(fetchedArticle);
        console.log("Fetched Article Data (raw from API):", JSON.stringify(fetchedArticle, null, 2));
      } catch (err: any) {
        console.error("Failed to fetch article:", err);
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          setError("Article not found.");
        } else {
          setError("Failed to load article. Please try again later.");
        }
        setArticle(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchArticle();
  }, [articleSlug]);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[60vh] text-foreground bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-lg text-muted-foreground">Loading article...</p>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="container mx-auto px-4 py-20 text-center min-h-[60vh] flex flex-col justify-center items-center text-foreground bg-background">
        <FileWarning className="h-16 w-16 text-destructive/70 mx-auto mb-6" />
        <h1 className="text-3xl font-bold mb-3 text-foreground">
          {error || "Article Not Found"}
        </h1>
        <p className="text-muted-foreground mb-8 max-w-md">
          {error
            ? "There was an issue loading the article."
            : "The article you are looking for does not exist or may have been moved."}
        </p>
        <Button
          asChild
          variant="outline"
          className="border-primary text-primary hover:bg-primary/10 hover:text-primary transition-colors"
        >
          <Link to="/articles">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Articles
          </Link>
        </Button>
      </div>
    );
  }

  const authorDisplay = placeholderAuthorImage(article.author_name);
  
  const unescapedBody = article.body ? unescapeHtml(article.body) : "<p>Article content not available.</p>";
  const unescapedSummary = article.summary ? unescapeHtml(article.summary) : null;
  
  // Construct the full image URL
  let fullImageUrl = null;
  if (article.image_url) {
    if (article.image_url.startsWith('http') || article.image_url.startsWith('/static/')) {
      // If it's already an absolute URL or correctly starts with /static/
      fullImageUrl = article.image_url.startsWith('/') ? `${backendBaseUrl}${article.image_url}` : article.image_url;
    } else {
      // If it's just the relative path like 'images/articles/...'
      fullImageUrl = `${backendBaseUrl}/static/${article.image_url}`;
    }
  }
  
  const authorImageUrl = (article as any).authorImageUrl; // Keep as any if not strictly typed

  return (
    <div className="bg-muted/30 dark:bg-muted/10 py-8 md:py-12 text-foreground">
      <div className="container mx-auto max-w-4xl bg-card p-6 md:p-10 rounded-lg shadow-xl border border-border/50">
        <div className="mb-6 print:hidden">
          <Button
            asChild
            variant="ghost"
            className="text-primary hover:text-primary/80 hover:bg-primary/5 dark:text-accent dark:hover:text-accent/80 dark:hover:bg-accent/10 transition-colors"
          >
            <Link to="/articles">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to All Articles
            </Link>
          </Button>
        </div>

        <header className="mb-8 border-b border-border pb-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 leading-tight text-gradient-maroon-to-gold">
            {article.title}
          </h1>
          <div className="flex flex-wrap items-center text-sm text-muted-foreground gap-x-4 gap-y-2 mb-6">
            <div className="flex items-center">
              {authorImageUrl ? (
                <Avatar className="h-8 w-8 mr-2 border border-border">
                  <AvatarImage
                    src={authorImageUrl} // Assuming this is already a full URL if it exists
                    alt={article.author_name || "Author"}
                  />
                  <AvatarFallback
                    style={{
                      backgroundColor: authorDisplay.fallbackBackgroundColor,
                      color: authorDisplay.fallbackTextColor,
                    }}
                    className="font-semibold"
                  >
                    {authorDisplay.initials}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <Avatar className="h-8 w-8 mr-2 border border-border">
                  <AvatarFallback
                    style={{
                      backgroundColor: authorDisplay.fallbackBackgroundColor,
                      color: authorDisplay.fallbackTextColor,
                    }}
                    className="font-semibold"
                  >
                    {authorDisplay.initials}
                  </AvatarFallback>
                </Avatar>
              )}
              <span className="font-medium text-foreground/90">
                {article.author_name || "Vedas Trading Team"}
              </span>
            </div>
            <div className="flex items-center">
              <CalendarDays className="h-4 w-4 mr-1.5 text-muted-foreground" />
              <span>Published on {formatDate(article.published_at)}</span>
            </div>
          </div>

          {unescapedSummary && (
            <p className="text-lg italic text-muted-foreground mb-6 border-l-4 border-primary/50 pl-4 py-2 bg-muted/50 dark:bg-muted/20 rounded-r-md">
              {unescapedSummary}
            </p>
          )}

          {/* Use the constructed fullImageUrl */}
          {fullImageUrl && (
            <img
              src={fullImageUrl}
              alt={`Hero image for ${article.title}`}
              className="w-full h-auto max-h-[450px] object-cover rounded-lg mt-8 shadow-lg border border-border/30"
            />
          )}
        </header>

        <article
          className="prose prose-lg lg:prose-xl max-w-none mx-auto 
                     prose-headings:text-foreground prose-headings:font-semibold 
                     prose-p:text-foreground/80 
                     prose-a:text-primary prose-a:font-medium hover:prose-a:text-accent prose-a:transition-colors
                     prose-strong:text-foreground prose-strong:font-semibold
                     prose-ul:list-disc prose-ol:list-decimal 
                     prose-li:marker:text-primary 
                     prose-blockquote:border-primary prose-blockquote:text-foreground/70
                     prose-figcaption:text-muted-foreground
                     dark:prose-invert dark:prose-headings:text-primary-foreground dark:prose-p:text-primary-foreground/70
                     dark:prose-a:text-accent dark:hover:prose-a:text-accent/80
                     dark:prose-strong:text-primary-foreground
                     dark:prose-li:marker:text-accent
                     dark:prose-blockquote:border-accent dark:prose-blockquote:text-primary-foreground/60
                     dark:prose-figcaption:text-muted-foreground"
          dangerouslySetInnerHTML={{ __html: unescapedBody }}
        />

        <footer className="mt-10 pt-8 border-t border-border">
          <div className="flex flex-wrap items-center gap-2">
            <Tag className="h-5 w-5 text-primary" />
            <span className="font-semibold mr-1 text-foreground">Tags:</span>
            {article.tags.map((tag: TagPublic) => (
              <Badge
                key={tag.id}
                variant="secondary"
                className="hover:bg-accent/10 hover:text-accent dark:hover:bg-accent/20 dark:hover:text-accent transition-colors"
              >
                <Link to={`/articles?tags=${encodeURIComponent(tag.name)}`}>
                  {tag.name}
                </Link>
              </Badge>
            ))}
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ArticleDetailPage;
